import { useNavigate, useSearch } from "@tanstack/react-router"
import React, { useState } from "react"
import toast from "react-hot-toast"

import { trpc } from "../../lib/trpc"
import { AccountSelector } from "./components/AccountSelector"
import { CampaignsTable } from "./components/CampaignsTable"
import { MetricsCards } from "./components/MetricsCards"

export function MetaAdsScreen() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { accountId?: string }

  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(search.accountId)
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0], // 30 days ago
    end: new Date().toISOString().split("T")[0], // today
  })

  // Queries
  const {
    data: accountsData,
    isLoading: accountsLoading,
    refetch: refetchAccounts,
  } = trpc.metaAds.listConnectedAccounts.useQuery()

  const { data: campaignsData, isLoading: campaignsLoading } = trpc.metaAds.getCampaigns.useQuery(
    { accountId: selectedAccountId },
    { enabled: !!selectedAccountId },
  )

  const { data: performanceData, isLoading: performanceLoading } = trpc.metaAds.getPerformanceReport.useQuery(
    {
      accountId: selectedAccountId,
      dateRange,
      level: "campaign",
    },
    { enabled: !!selectedAccountId },
  )

  const syncMetricsMutation = trpc.metaAds.syncAdMetrics.useMutation({
    onSuccess: (data) => {
      toast.success(`Synced ${data.synced} metrics successfully`)
      if (data.errors.length > 0) {
        data.errors.forEach((error) => toast.error(error))
      }
    },
    onError: (error) => {
      toast.error(`Failed to sync metrics: ${error.message}`)
    },
  })

  const connectedAccounts = accountsData?.accounts || []
  const primaryAccount = connectedAccounts.find((acc) => acc.isPrimary)
  const campaigns = campaignsData?.campaigns || []

  // Auto-select primary account if none selected
  React.useEffect(() => {
    if (!selectedAccountId && primaryAccount) {
      setSelectedAccountId(primaryAccount.accountId)
      navigate({
        to: "/meta-ads",
        // @ts-ignore
        search: { accountId: primaryAccount.accountId },
        replace: true,
      })
    }
  }, [primaryAccount, selectedAccountId, navigate])

  const handleAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId)
    navigate({
      to: "/meta-ads",
      // @ts-ignore
      search: { accountId },
    })
  }

  const handleSyncMetrics = () => {
    if (!selectedAccountId) {
      toast.error("Please select an account first")
      return
    }

    syncMetricsMutation.mutate({
      accountId: selectedAccountId,
      dateRange,
    })
  }

  if (accountsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading Meta Ads accounts...</p>
        </div>
      </div>
    )
  }

  if (connectedAccounts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-4">
          <div className="mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">No Meta Ad Accounts</h1>
            <p className="text-muted-foreground mb-6">
              Add your Meta Ads accounts through Companies to start managing campaigns and tracking performance.
            </p>
            <button
              onClick={() => navigate({ to: "/companies" })}
              className="bg-primary text-primary-foreground px-6 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              Go to Companies
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Meta Ads Manager</h1>
            <p className="text-muted-foreground">Manage your Meta advertising campaigns and performance</p>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handleSyncMetrics}
              disabled={syncMetricsMutation.isLoading || !selectedAccountId}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncMetricsMutation.isLoading ? "Syncing..." : "Sync Metrics"}
            </button>

            <button
              onClick={() => navigate({ to: "/companies" })}
              className="border border-border text-foreground px-4 py-2 rounded-md hover:bg-accent transition-colors"
            >
              Manage Accounts
            </button>
          </div>
        </div>

        {/* Account Selector */}
        <div className="mb-6">
          <AccountSelector
            accounts={connectedAccounts as any}
            selectedAccountId={selectedAccountId}
            onAccountChange={handleAccountChange}
          />
        </div>

        {selectedAccountId && (
          <>
            {/* Date Range Selector */}
            <div className="mb-6 flex items-center gap-4">
              <label className="text-sm font-medium text-foreground">Date Range:</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                className="border border-border rounded-md px-3 py-1 text-sm bg-background text-foreground"
              />
              <span className="text-muted-foreground">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                className="border border-border rounded-md px-3 py-1 text-sm bg-background text-foreground"
              />
            </div>

            {/* Metrics Cards */}
            <div className="mb-8">
              <MetricsCards data={performanceData} isLoading={performanceLoading} />
            </div>

            {/* Campaigns Table */}
            <div className="bg-card rounded-lg border border-border">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-card-foreground">Campaigns</h2>
                <p className="text-sm text-muted-foreground">Active campaigns in your selected account</p>
              </div>
              <div className="p-6">
                <CampaignsTable
                  campaigns={campaigns}
                  isLoading={campaignsLoading}
                  onCampaignClick={(campaign) => {
                    navigate({
                      to: "/meta-ads/campaigns/$campaignId",
                      params: {
                        // @ts-ignore
                        campaignId: campaign.id,
                      },
                    })
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
